import { ChangeEvent, MutableRefObject, forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { houseNumberFirst } from '../../useCountries';
import Address from '../../Common/Address';
import CountryOptions from '../../Common/CountryOptions';
import GetAddressQuery from '../../GetAddressQuery';
import ParticipantAttributeInputProps from '../ParticipantAttributeInputProps';
import UI from '../../UI';
import useLazyQuery from '../../../api/useLazyQuery';

export interface AddressInputValue {
  street?: string | null;
  house_number?: string | null;
  zip_code?: string | null;
  city?: string | null;
  extra_address_line?: string | null;
  country?: string | null;
}

const AddressInput = ({
  value, onChange, onBlur, touched, required = true, errors, idPrefix = 'Participant',
}: ParticipantAttributeInputProps<AddressInputValue>) => {
  const { t } = useTranslation('common');

  const handleChange = (field: keyof AddressInputValue) => (
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange({ [field]: event.target.value });
    }
  );

  // Auto-fill street and city based on zip code and house number.
  const streetRef = useRef<HTMLInputElement>();
  const cityRef = useRef<HTMLInputElement>();

  // Initially toggle manual input mode when just street or city is missing.
  const [manualInput, setManualInput] = useState<boolean>(
    !!value.country && !!value.zip_code && !!value.house_number && (!value.street || !value.city),
  );

  const [getAddress] = useLazyQuery(GetAddressQuery);

  const prevCountryRef = useRef(value.country);
  const prevZipCodeRef = useRef(value.zip_code);
  const prevHouseNumberRef = useRef(value.house_number);

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // When country, postal code or house number changes, and the country is NL, fetch the address.
  // For this effect to work correctly, the onChange callback should be memoized (by the parent).
  // Since there is no guarantee that this will always be the case, the onChange callback is stored
  // in a ref, so we can avoid listing it as a dependency of the effect.
  // React 19 has an official solution for this: https://react.dev/reference/react/useEffectEvent
  useEffect(() => {
    const addressChanged = value.country !== prevCountryRef.current
      || value.zip_code !== prevZipCodeRef.current
      || value.house_number !== prevHouseNumberRef.current;

    if (!addressChanged) {
      return undefined;
    }

    let subscribed = true;

    if (value.country === 'NL') {
      if (value.zip_code && value.house_number) {
        // Autocomplete the address.
        getAddress({
          variables: {
            country: value.country,
            zip_code: value.zip_code,
            house_number: value.house_number,
          },
        }).then((response) => {
          if (!subscribed) {
            return;
          }

          const address = response.data.getAddress;
          const newValue: Partial<AddressInputValue> = {};

          setManualInput(address.valid === false);

          if (address.valid) {
            newValue.street = address.street;

            if (streetRef.current) {
              streetRef.current.value = address.street;
            }

            newValue.city = address.city;

            if (cityRef.current) {
              cityRef.current.value = address.city;
            }

            onChangeRef.current({ ...newValue, extra_address_line: null });
          }
        }).catch(() => {
          // When the address could not be fetched, show all inputs.
          setManualInput(true);
        });
      } else if (!manualInput) {
        // If the postal code or house number is missing, reset the street and city fields,
        // but only if the user is not manually editing these fields.
        onChangeRef.current({ street: null, city: null, extra_address_line: null });

        if (streetRef.current) {
          streetRef.current.value = '';
        }

        if (cityRef.current) {
          cityRef.current.value = '';
        }
      } else {
        // Finally, always unset the extra address line for Dutch addresses.
        onChangeRef.current({ extra_address_line: null });
      }
    }

    prevCountryRef.current = value.country;
    prevZipCodeRef.current = value.zip_code;
    prevHouseNumberRef.current = value.house_number;

    return () => {
      subscribed = false;
    };
  }, [value, getAddress, manualInput]);

  const inputProps = {
    idPrefix,
    value,
    errors,
    touched,
    required,
    onBlur,
  };

  return (
    <UI.FormGrid>
      <UI.InputGroup sc={{ valid: !errors?.country, touched: touched?.country }}>
        <UI.InputLabel htmlFor={`${idPrefix}Country`}>
          {t('participant_attributes.country')}
          {required && (
            <>
              {' '}
              <UI.RequiredMark />
            </>
          )}
        </UI.InputLabel>
        <UI.Select
          value={value.country || ''}
          onChange={(event) => onChange({ country: event.target.value || null })}
          onBlur={() => onBlur?.('country')}
          id={`${idPrefix}Country`}
        >
          <option value=""> </option>
          <CountryOptions />
        </UI.Select>
        <UI.ErrorMessages
          attribute={t('participant_attributes.country')}
          errors={errors?.country}
        />
      </UI.InputGroup>
      {value.country === 'NL' ? (
        <>
          <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
            <ZipCodeInput
              onChange={handleChange('zip_code')}
              {...inputProps}
            />
            <HouseNumberInput
              onChange={handleChange('house_number')}
              {...inputProps}
            />
          </UI.FormGrid>

          {!manualInput && value.street && value.city && (
            <UI.Info>
              <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
                <UI.Div>
                  <Address {...value} />
                </UI.Div>
                <UI.Div sc={{ textAlign: 'right' }}>
                  <UI.Button
                    onClick={() => setManualInput(true)}
                    sc={{ brand: 'secondary', size: 'sm' }}
                  >
                    {t('edit')}
                  </UI.Button>
                </UI.Div>
              </UI.FlexContainer>
            </UI.Info>
          )}

          {manualInput && (
            <>
              <StreetInput
                onChange={handleChange('street')}
                ref={streetRef}
                {...inputProps}
              />
              <CityInput
                onChange={handleChange('city')}
                ref={cityRef}
                {...inputProps}
              />
              <UI.Warning>
                {t('participant_attributes.address_not_verified_warning')}
              </UI.Warning>
            </>
          )}
        </>
      ) : (
        <>
          {houseNumberFirst.includes(value.country) ? (
            <UI.FormGrid sc={{ columns: '2fr 3fr' }}>
              <HouseNumberInput
                onChange={handleChange('house_number')}
                {...inputProps}
              />
              <StreetInput
                onChange={handleChange('street')}
                ref={streetRef}
                {...inputProps}
              />
            </UI.FormGrid>
          ) : (
            <UI.FormGrid sc={{ columns: '3fr 2fr' }}>
              <StreetInput
                onChange={handleChange('street')}
                ref={streetRef}
                {...inputProps}
              />
              <HouseNumberInput
                onChange={handleChange('house_number')}
                {...inputProps}
              />
            </UI.FormGrid>
          )}

          <ExtraAddressLineInput
            onChange={handleChange('extra_address_line')}
            {...inputProps}
          />

          <UI.FormGrid sc={{ columns: '2fr 3fr' }}>
            <ZipCodeInput
              onChange={handleChange('zip_code')}
              {...inputProps}
            />
            <CityInput
              onChange={handleChange('city')}
              ref={cityRef}
              {...inputProps}
            />
          </UI.FormGrid>
        </>
      )}
    </UI.FormGrid>
  );
};

type InputProps<InputName extends keyof AddressInputValue> =
  Omit<ParticipantAttributeInputProps<Pick<AddressInputValue, InputName>>, 'onChange'>
  & Record<'onChange', (event: ChangeEvent<HTMLInputElement>) => void>;

const StreetInput = forwardRef(({
  onChange, idPrefix, value, errors, touched, required, onBlur,
}: InputProps<'street'>, ref: MutableRefObject<HTMLInputElement>) => {
  const { t } = useTranslation('common');

  return (
    <UI.InputGroup
      sc={{ valid: !errors?.street, touched: touched?.street }}
      key="street"
    >
      <UI.InputLabel htmlFor={`${idPrefix}Street`}>
        {t('participant_attributes.street')}
        {required && (
          <>
            {' '}
            <UI.RequiredMark />
          </>
        )}
      </UI.InputLabel>
      <UI.DebouncedInput
        value={value.street}
        onChange={onChange}
        onBlur={() => onBlur?.('street')}
        id={`${idPrefix}Street`}
        ref={ref}
      />
      <UI.ErrorMessages
        attribute={t('participant_attributes.street')}
        errors={errors?.street}
      />
    </UI.InputGroup>
  );
});

const HouseNumberInput = ({
  onChange, idPrefix, value, errors, touched, required, onBlur,
}: InputProps<'house_number'>) => {
  const { t } = useTranslation('common');

  return (
    <UI.InputGroup
      sc={{ valid: !errors?.house_number, touched: touched?.house_number }}
      key="house_number"
    >
      <UI.InputLabel htmlFor={`${idPrefix}HouseNumber`}>
        {t('participant_attributes.house_number')}
        {required && (
          <>
            {' '}
            <UI.RequiredMark />
          </>
        )}
      </UI.InputLabel>
      <UI.DebouncedInput
        value={value.house_number}
        onChange={onChange}
        onBlur={() => onBlur?.('house_number')}
        id={`${idPrefix}HouseNumber`}
      />
      <UI.ErrorMessages
        attribute={t('participant_attributes.house_number')}
        errors={errors?.house_number}
      />
    </UI.InputGroup>
  );
};

const ExtraAddressLineInput = ({
  onChange, idPrefix, value, errors, touched, onBlur,
}: InputProps<'extra_address_line'>) => {
  const { t } = useTranslation('common');

  return (
    <UI.InputGroup
      sc={{ valid: !errors?.extra_address_line, touched: touched?.extra_address_line }}
    >
      <UI.InputLabel htmlFor={`${idPrefix}ExtraAddressLine`}>
        {t('participant_attributes.extra_address_line')}
      </UI.InputLabel>
      <UI.DebouncedInput
        value={value.extra_address_line}
        onChange={onChange}
        onBlur={() => onBlur?.('extra_address_line')}
        placeholder={t('participant_attributes.extra_address_line_placeholder')}
        id={`${idPrefix}ExtraAddressLine`}
      />
      <UI.ErrorMessages
        attribute={t('participant_attributes.extra_address_line')}
        errors={errors?.extra_address_line}
      />
    </UI.InputGroup>
  );
};

const ZipCodeInput = ({
  onChange, idPrefix, value, errors, touched, required, onBlur,
}: InputProps<'zip_code'>) => {
  const { t } = useTranslation('common');

  return (
    <UI.InputGroup
      sc={{ valid: !errors?.zip_code, touched: touched?.zip_code }}
      key="zip_code"
    >
      <UI.InputLabel htmlFor={`${idPrefix}ZipCode`}>
        {t('participant_attributes.zip_code')}
        {required && (
          <>
            {' '}
            <UI.RequiredMark />
          </>
        )}
      </UI.InputLabel>
      <UI.DebouncedInput
        value={value.zip_code}
        onChange={onChange}
        onBlur={() => onBlur?.('zip_code')}
        id={`${idPrefix}ZipCode`}
      />
      <UI.ErrorMessages
        attribute={t('participant_attributes.zip_code')}
        errors={errors?.zip_code}
      />
    </UI.InputGroup>
  );
};

const CityInput = forwardRef(({
  onChange, idPrefix, value, errors, touched, required, onBlur,
}: InputProps<'city'>, ref: MutableRefObject<HTMLInputElement>) => {
  const { t } = useTranslation('common');

  return (
    <UI.InputGroup
      sc={{ valid: !errors?.city, touched: touched?.city }}
      key="city"
    >
      <UI.InputLabel htmlFor={`${idPrefix}City`}>
        {t('participant_attributes.city')}
        {required && (
          <>
            {' '}
            <UI.RequiredMark />
          </>
        )}
      </UI.InputLabel>
      <UI.DebouncedInput
        value={value.city}
        onChange={onChange}
        onBlur={() => onBlur?.('city')}
        id={`${idPrefix}City`}
        ref={ref}
      />
      <UI.ErrorMessages
        attribute={t('participant_attributes.city')}
        errors={errors?.city}
      />
    </UI.InputGroup>
  );
});

export default AddressInput;
